/**
 * Computer-keyboard → note mappings.
 *
 * The mimetic ("rows") layout exploits the fact that a staggered QWERTY
 * keyboard has the same geometry as a piano: each home-row key sits half a key
 * width up-and-right of a bottom-row key, exactly where a black key sits
 * relative to its white neighbour. So the bottom row's whites (Z X C V B N M)
 * get their sharps on the home row (S D _ G H _ …), the gaps falling where a
 * piano has no black key; the same trick applies on the QWERTY/number rows.
 */

/**
 * Row-aligned mode: the computer's two lower rows serve the piano's lower
 * display row, its two upper rows serve the upper one.
 *
 * Every white-key pair on QWERTY has a key sitting between it (`s d f g h j k
 * l ;` on the home row, `2`…`=` on the number row), so the black/white
 * mimicry survives whatever note a row happens to start on — the unbound
 * positions simply move to wherever the piano has no black key. That is what
 * makes this mode possible at all, but it does cost the C-plays-C mnemonic,
 * since each block must start where its screen row starts.
 *
 * Capacity: 10 whites below, 12 above — 22 in total, about 3.1 octaves.
 */
export interface RowBlock {
  readonly whites: readonly string[];
  /**
   * blacks[i] sits between whites[i] and whites[i + 1] — and blacks[n - 1]
   * sits just past whites[n - 1], which is needed because a display row can
   * end on a black key (a row running A4..G#5 finishes on G#5).
   */
  readonly blacks: readonly string[];
  /** The key half a step to the left of whites[0]. */
  readonly blackBefore: string;
}

export const ROW_BLOCKS: readonly RowBlock[] = [
  {
    whites: ['z', 'x', 'c', 'v', 'b', 'n', 'm', ',', '.', '/'],
    // ' sits half a key right of /, so it covers a black above the last white.
    blacks: ['s', 'd', 'f', 'g', 'h', 'j', 'k', 'l', ';', "'"],
    blackBefore: 'a',
  },
  {
    // Ends at p. [ and ] are dropped so the block stays geometrically whole:
    // their sharps would be − and =, which are reserved for shifting in every
    // layout (see OCTAVE_*_KEYS). Keeping them as whites-without-sharps left a
    // hole wherever a wide window reached that far, so the block is 10 whites
    // with a full set of 9 sharps instead — every white here has its black.
    whites: ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    blacks: ['2', '3', '4', '5', '6', '7', '8', '9', '0'],
    blackBefore: '1',
  },
];

/**
 * Keys that slide the mapping window. − / = / arrows move it a whole octave;
 * their shifted forms _ / + move it a single white key. None of these is ever
 * bound as a note, so they can be intercepted before note handling.
 */
export const OCTAVE_DOWN_KEYS: readonly string[] = ['-', 'arrowleft'];
export const OCTAVE_UP_KEYS: readonly string[] = ['=', 'arrowright'];
export const WHITE_DOWN_KEYS: readonly string[] = ['_'];
export const WHITE_UP_KEYS: readonly string[] = ['+'];
/** One octave of white keys — the step for an octave-sized slide. */
export const ROW_SHIFT_STEP = 7;
